import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  Layers,
  FileCode,
  GitBranch,
  Database,
  Settings,
  ClipboardList,
  Building2,
  GraduationCap,
  Briefcase,
  HeartPulse,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import WebCenterStackDiagram from '@/components/diagrams/WebCenterStackDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const SCENARIOS = [
  {
    icon: HeartPulse,
    role: 'IT Manager · Healthcare',
    quote:
      'Our non-PO coding form is built in Oracle ADF and customizations turn every small change into a development project. Can we modernize without a full ADF rebuild?',
    answer: (
      <>
        <strong className="text-accent">Yes.</strong> The ADF non-PO coding form is one of the
        WebCenter AP customizations we hear about most. EZ Cloud delivers a modern, configurable
        coding and approval experience that your team can adjust without ADF development cycles —
        while preserving your existing Oracle EBS or Fusion integration.
      </>
    ),
  },
  {
    icon: GraduationCap,
    role: 'CIO · K-12 District',
    quote:
      "We're planning the move from Oracle EBS to Oracle Fusion. Our AP automation complexity is one of the things making the migration harder. Can modernizing AP first unlock the Fusion migration?",
    answer: (
      <>
        <strong className="text-accent">This is a scenario we handle directly.</strong> AP
        automation modernization is often the unlock that makes the broader EBS → Fusion migration
        tractable. EZ Cloud can deploy on EBS first, then bridge to Fusion as part of the migration
        — same platform, same configuration, no second implementation.
      </>
    ),
  },
  {
    icon: Building2,
    role: 'Finance Director · Higher Education',
    quote:
      'Our WebCenter AP implementation has grown sophisticated over a decade. Documentation grew sparse and the original implementers may no longer be available. How would you approach discovery?',
    answer: (
      <>
        <strong className="text-accent">We read the implementation directly.</strong> Our team is
        fluent across the full WebCenter stack — Forms Recognition rules, SOA composites, WebLogic
        deployments, FIPSA configurations, ADF customizations. We extract the business logic from
        the artifacts. Your team participates at validation and UAT, not at reconstructing
        historical decisions.
      </>
    ),
  },
  {
    icon: Briefcase,
    role: 'CFO · Mid-Market Manufacturer',
    quote:
      "We want AI-powered invoice processing and supplier email handling that WebCenter doesn't do natively. Can we layer something modern on top without disrupting our Oracle posture?",
    answer: (
      <>
        <strong className="text-accent">Yes.</strong> EZ Cloud adds AI invoice extraction,
        supplier-email exception prevention, and modern automation while preserving Oracle-supported
        integration patterns. Your audit trail, security model, and Oracle support relationship are
        preserved. We deploy via the same Oracle-supported integration interfaces Oracle itself
        documents.
      </>
    ),
  },
];

const STACK_COLUMNS = [
  {
    icon: FileCode,
    title: 'Capture & recognition',
    items: [
      'Oracle WebCenter Enterprise Capture',
      'Oracle WebCenter Forms Recognition',
      'Oracle WebCenter Imaging',
      'FIPSA — Financial Imaging Processing Solutions Accelerator',
    ],
  },
  {
    icon: GitBranch,
    title: 'Workflow & integration',
    items: ['Oracle SOA Suite', 'Oracle WebLogic Server', 'Oracle WebCenter Content'],
  },
  {
    icon: Database,
    title: 'ERP integration',
    items: [
      'Oracle E-Business Suite Payables',
      'Oracle Fusion Cloud ERP (Payables / OIC)',
      'Oracle ADF customizations',
    ],
  },
  {
    icon: Settings,
    title: 'UX customizations',
    items: [
      'Oracle ADF non-PO coding forms',
      'Custom approval workflows',
      'Hold codes & exception handling',
    ],
  },
];

const HOW_IT_WORKS = [
  {
    title: 'Discovery from the artifacts',
    body: 'Forms Recognition rules, SOA composites, WebLogic deployments, FIPSA configurations, ADF customizations — we extract the working business logic directly from the running system.',
  },
  {
    title: 'Deploys on Oracle EBS or Oracle Fusion',
    body: 'EZ Cloud runs natively against both. Keep your existing ERP, or use AP modernization as the bridge to Fusion as part of a planned migration.',
  },
  {
    title: 'Customer involvement at testing & UAT',
    body: 'Your AP staff, IT team, and finance leaders participate where their input matters most — validating that the modernized system handles real invoices and real workflows correctly.',
  },
  {
    title: 'Oracle-supported integration patterns',
    body: 'Oracle Payables Open Interface on EBS, Oracle Integration Cloud + FBDI on Fusion. No base-table writes. Your Oracle support posture, audit trail, and security model are preserved.',
  },
];

const PATH_STEPS = [
  {
    step: '1',
    title: 'Read the existing implementation',
    body: 'Forms Recognition, SOA composites, WebLogic config, FIPSA accelerators, ADF customizations, EBS integration code.',
  },
  {
    step: '2',
    title: 'Translate the requirements forward',
    body: 'Business rules, approval flows, hold codes, exception handling — mapped to modern equivalents.',
  },
  {
    step: '3',
    title: 'Build on EZ Cloud — on EBS or Fusion',
    body: 'Native integration via Oracle-supported interfaces — same Oracle support posture you have today.',
  },
  {
    step: '4',
    title: 'Your team validates in UAT',
    body: 'Customer involvement concentrated where it matters — testing the modernized result with real invoices.',
  },
];

const ASSESSMENT_PILLS = [
  {
    title: 'What we read',
    body: (
      <>
        <strong className="text-gray-900">Your existing WebCenter implementation.</strong> Forms
        Recognition projects, SOA composites, WebLogic configuration, FIPSA accelerators, ADF
        customizations, and the EBS or Fusion integration code.
      </>
    ),
  },
  {
    title: 'What you give us',
    body: (
      <>
        <strong className="text-gray-900">Read access and a kickoff call.</strong> No documentation
        hunt. No workshops to reconstruct decisions. Your team's time is reserved for the validation
        phase.
      </>
    ),
  },
  {
    title: 'What you get back',
    body: (
      <>
        <strong className="text-gray-900">A forward-modernization plan.</strong> Inventoried current
        state, mapped to a modern equivalent on EZ Cloud, deployable on EBS or Fusion, with a
        realistic delivery timeline.
      </>
    ),
  },
];

const FAQS = [
  {
    q: 'What is Oracle WebCenter AP modernization?',
    a: 'Oracle WebCenter AP modernization is the work of replacing a legacy Oracle WebCenter accounts payable implementation — Enterprise Capture, Forms Recognition, Imaging, Content, SOA composites, WebLogic deployments, FIPSA accelerators, and ADF customizations — with a modern AI-driven AP automation platform. The modernized solution preserves the Oracle support posture, audit trail, and integration patterns the business relies on, and deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP.',
  },
  {
    q: 'What is Oracle WebCenter Forms Recognition (WFR)?',
    a: 'Oracle WebCenter Forms Recognition (WFR) is the data-extraction layer in the Oracle WebCenter AP stack. It uses per-supplier templates to recognize invoice fields, with a verifier queue for confidence-based review. Modernization replaces the template-based model with AI extraction that reads unseen invoices, scores confidence per field, and learns from corrections — eliminating the per-supplier template maintenance burden.',
  },
  {
    q: 'What is FIPSA in Oracle WebCenter AP?',
    a: 'FIPSA — the Financial Imaging Processing Solutions Accelerator — is an Oracle-published accelerator that codifies the WebCenter AP imaging workflow for Oracle E-Business Suite Payables. It combines WebCenter Imaging, Forms Recognition, SOA Suite, and EBS integration into a deployable reference implementation. Modernization absorbs the FIPSA workflow shape into a configuration-driven AI pipeline.',
  },
  {
    q: 'Do we have to move to Oracle Fusion at the same time?',
    a: 'No. EZ Cloud deploys natively against Oracle E-Business Suite and Oracle Fusion Cloud ERP. Many customers modernize their AP automation and stay on Oracle EBS. Others use the AP modernization as part of a planned Fusion migration — the same EZ Cloud platform works both before and after.',
  },
  {
    q: 'What about our ADF non-PO coding form customizations?',
    a: 'The Oracle ADF non-PO coding form is one of the customizations our customers most often want to modernize — small changes tend to become development projects. EZ Cloud delivers a modern coding and approval experience that is configurable without ADF development cycles, while preserving the business logic and approval routing your team relies on. We read the existing ADF customizations to make sure nothing important is lost in translation.',
  },
  {
    q: 'Our documentation is sparse. Is that a problem?',
    a: "It's the most common situation we encounter, and it's not a blocker. We read the WebCenter artifacts directly — Forms Recognition rules, SOA composites, WebLogic deployments, FIPSA accelerators, EBS integration code. The configuration is the source of truth. We extract the business logic from there.",
  },
  {
    q: 'What does our team need to do?',
    a: "Read access to your existing WebCenter environments for the assessment phase, then validation in UAT once we've built the modernized system. Your AP staff doesn't need to reconstruct historical decisions, and your IT team doesn't need to find original implementers.",
  },
  {
    q: 'How does this preserve our Oracle support posture?',
    a: 'EZ Cloud deploys via Oracle-supported integration interfaces — Oracle Payables Open Interface on EBS, Oracle Integration Cloud and FBDI on Fusion. No base-table writes. Your audit trail, security model, and Oracle support relationship are preserved.',
  },
  {
    q: 'What versions of WebCenter do you work with?',
    a: '11g, 12c, and 14c. If your implementation pre-dates 11g, we can still help — the older versions have particular configuration patterns we have seen many times.',
  },
];

const ModernizeOracleWebCenter = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  // TechArticle schema — establishes the page as authoritative content with
  // a named expert author, so AI Overviews and rich results can cite it.
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Modernize Oracle WebCenter AP Automation',
    description:
      'A modernization path for Oracle WebCenter AP automation — Forms Recognition, SOA, WebLogic, FIPSA, ADF — across 11g, 12c, and 14c. Deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/modernize-oracle-webcenter',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Content' },
      { '@type': 'Thing', name: 'Oracle WebCenter Imaging' },
      { '@type': 'Thing', name: 'Oracle WebCenter Forms Recognition' },
      { '@type': 'Thing', name: 'Oracle WebCenter Enterprise Capture' },
      { '@type': 'Thing', name: 'Oracle SOA Suite' },
      { '@type': 'Thing', name: 'Oracle WebLogic Server' },
      { '@type': 'Thing', name: 'Oracle ADF' },
      { '@type': 'Thing', name: 'FIPSA' },
      { '@type': 'Thing', name: 'Oracle E-Business Suite' },
      { '@type': 'Thing', name: 'Oracle Fusion Cloud ERP' },
    ],
  };

  // Service schema — describes the productized WebCenter AP Modernization
  // Assessment so search engines treat it as a real offering, not marketing copy.
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Oracle WebCenter AP Modernization Assessment',
    serviceType: 'Oracle WebCenter AP modernization consulting',
    provider: { '@id': 'https://ezcloud.ai/#organization' },
    areaServed: ['US', 'CA', 'GB', 'AU', 'NZ'],
    description:
      'A fixed-scope, fixed-fee engagement that produces a forward-modernization plan for an existing Oracle WebCenter AP implementation. Covers Forms Recognition, SOA composites, WebLogic deployments, FIPSA accelerators, ADF customizations, and the EBS or Fusion integration code. Across WebCenter 11g, 12c, and 14c.',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Finance and IT leaders running Oracle WebCenter AP automation',
    },
    category: 'Enterprise software consulting',
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter AP Modernization — 11g, 12c, 14c | EZ Cloud"
        description="Oracle WebCenter AP modernization across Enterprise Capture, Forms Recognition, SOA, WebLogic, FIPSA, and ADF — versions 11g, 12c, and 14c. Deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP using Oracle-supported integration patterns. Founder-led, 25 years of Oracle WebCenter AP expertise."
        canonical="https://ezcloud.ai/solutions/modernize-oracle-webcenter"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Modernize Oracle WebCenter AP',
            url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Layers className="w-4 h-4" aria-hidden />
              Solutions · Oracle WebCenter AP Modernization
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Modernize Oracle WebCenter AP Automation.
              <span className="block text-accent mt-3">25 years across the full stack.</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              From Enterprise Capture and Forms Recognition to SOA, WebLogic, FIPSA, Content, and
              Imaging — across 11g, 12c, and 14c. Deploys on Oracle E-Business Suite or Oracle
              Fusion Cloud ERP, or bridges the two as part of a planned migration.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-white/10 border border-white/20 text-sm text-white/90">
              <CheckCircle2 className="w-4 h-4 text-accent" aria-hidden />
              <span>
                Published on the{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
                >
                  Oracle Reference Architecture for OCI
                </a>{' '}
                · featured in{' '}
                <a
                  href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
                >
                  Oracle&rsquo;s technical case studies
                </a>
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                Book a 30-minute discovery call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <a href="#how">See how it works</a>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR / definitional summary — primes AI Overview synthesis with
            declarative quotable statements. Specific Oracle product terminology
            and version numbers density-optimized for AEO. */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle WebCenter AP modernization, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter AP modernization is the work of replacing a legacy Oracle
                  WebCenter accounts payable implementation with a modern, AI-driven AP automation
                  platform — while preserving the Oracle support posture, audit trail, and ERP
                  integration patterns the customer relies on.
                </strong>{' '}
                It covers Oracle WebCenter Enterprise Capture (WEC), WebCenter Forms Recognition
                (WFR), WebCenter Content (WCC), WebCenter Imaging (WCI), Financial Imaging Processing
                Solutions Accelerator (FIPSA), Oracle SOA Suite composites, Oracle WebLogic Server
                deployments, and Oracle ADF customizations — across WebCenter 11g, 12c, and 14c.
              </p>
              <p>
                EZ Cloud delivers Oracle WebCenter AP modernization by reading the existing
                implementation directly — Forms Recognition projects, SOA composites, WebLogic
                configuration, FIPSA accelerators, ADF non-PO coding forms — and translating the
                business logic forward to a modern AP automation platform. The modernized solution
                deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP, posting via
                Oracle-supported integration interfaces: Oracle Payables Open Interface on EBS,
                Oracle Integration Cloud and FBDI on Fusion. No base-table writes.
              </p>
              <p>
                Founder-led by{' '}
                <a
                  href="#founder"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Andrew Blackman
                </a>
                , a 25-year Oracle WebCenter AP specialist. EZ Cloud's reference architecture for
                Oracle Cloud Infrastructure is published on{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
                >
                  docs.oracle.com
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Decision Guide CTA banner — prominent placement right after the
            definitional block. Captures buyers in the awareness phase before
            they descend into the specific scenarios. */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-16 h-16 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-8 h-8 text-accent" aria-hidden />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Free resource
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  The Oracle WebCenter AP December 2026 Decision Guide
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A structured guide for finance, IT, and operational leaders facing the Fusion
                  Middleware 12c support cliff. Four forward paths, stack-specific considerations
                  (FIPSA, WFR, ADF, SOA), eight diagnostic questions, vendor evaluation checklist.
                </p>
              </div>
              <Link
                to="/resources/webcenter-decision-guide"
                className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg flex-shrink-0"
              >
                Read the Decision Guide
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Scenarios */}
        <section id="scenarios" className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The real questions WebCenter AP customers are asking
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the conversations we have most often with finance, IT, and operational
              leaders running Oracle WebCenter AP automation today. If any of these sound like your
              situation, we can probably help.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, role, quote, answer }) => (
                <Card key={role} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500 mb-4">
                    <Icon className="w-4 h-4 text-accent" aria-hidden />
                    {role}
                  </div>
                  <p className="text-lg font-bold text-gray-900 mb-4 leading-snug">
                    <span className="text-accent">&ldquo;</span>
                    {quote}
                    <span className="text-accent">&rdquo;</span>
                  </p>
                  <p className="text-gray-700 text-[15px] leading-relaxed">{answer}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Stack */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The full WebCenter AP stack
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Deep expertise across every layer
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
              The Oracle WebCenter AP automation stack spans multiple products, each with its own
              configuration model. EZ Cloud's founder has worked across every layer of this stack
              for 25 years.
            </p>

            <div className="max-w-5xl mx-auto mb-10">
              <WebCenterStackDiagram />
            </div>

            <Card className="p-8 md:p-12 bg-white border border-gray-200 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Where we work, day to day</h3>
              <p className="text-gray-600 mb-8 max-w-3xl">
                When we read your existing implementation, we work with each of these products
                directly — reading Forms Recognition projects, SOA composites, WebLogic deployments,
                FIPSA accelerators, and the EBS or Fusion integration code.
              </p>
              <div className="grid sm:grid-cols-2 gap-8">
                {STACK_COLUMNS.map(({ icon: Icon, title, items }) => (
                  <div key={title}>
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-accent mb-3">
                      <Icon className="w-4 h-4" aria-hidden />
                      {title}
                    </div>
                    <ul className="space-y-2">
                      {items.map((item) => (
                        <li
                          key={item}
                          className="text-sm text-gray-900 font-medium bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200 text-gray-700 text-sm">
                <strong className="text-gray-900 font-bold">Version coverage</strong>
                <span className="inline-flex gap-2 ml-3 align-middle">
                  <span className="bg-accent/10 border border-accent/30 text-accent font-bold px-3 py-0.5 rounded-full text-xs">
                    11g
                  </span>
                  <span className="bg-accent/10 border border-accent/30 text-accent font-bold px-3 py-0.5 rounded-full text-xs">
                    12c
                  </span>
                  <span className="bg-accent/10 border border-accent/30 text-accent font-bold px-3 py-0.5 rounded-full text-xs">
                    14c
                  </span>
                </span>
                <span className="ml-2">If your implementation pre-dates 11g, we can still help.</span>
              </div>
            </Card>
          </div>
        </section>

        {/* Comparison table — WebCenter AP → EZ Cloud mapping. Highly
            AI-Overview friendly format (Google's synthesis quotes tables
            verbatim). Anchors the modernization narrative in one visual. */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The modernization mapping
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Oracle WebCenter AP &rarr; EZ Cloud
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              How each layer of the Oracle WebCenter AP stack maps forward in a modernization. The
              business logic is preserved. The maintenance burden is not.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/3">
                      Capability
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/3">
                      Oracle WebCenter today
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/3">
                      EZ Cloud modernized
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    {
                      cap: 'Invoice capture',
                      wc: 'WebCenter Enterprise Capture (WEC) + WebCenter Imaging',
                      ez: 'AI extraction with confidence scoring per field, learning from corrections',
                    },
                    {
                      cap: 'Data extraction',
                      wc: 'WebCenter Forms Recognition (WFR) — per-supplier templates, verifier queue',
                      ez: 'AI-grade extraction across PO and non-PO invoices, no template maintenance',
                    },
                    {
                      cap: 'Document storage & retrieval',
                      wc: 'WebCenter Content + Managed Attachments',
                      ez: 'OCI Object Storage with signed-URL retrieval from inside the ERP',
                    },
                    {
                      cap: 'Workflow orchestration',
                      wc: 'Oracle SOA Suite composites + WebLogic Server',
                      ez: 'Configuration-driven workflow with per-approver queues and tolerance rules',
                    },
                    {
                      cap: 'Non-PO coding UI',
                      wc: 'Oracle ADF custom form (development-heavy to change)',
                      ez: 'Configurable coding screen — no ADF development cycle',
                    },
                    {
                      cap: 'Imaging accelerator',
                      wc: 'FIPSA (Financial Imaging Processing Solutions Accelerator)',
                      ez: 'Native AI pipeline absorbing the FIPSA workflow shape',
                    },
                    {
                      cap: 'EBS integration',
                      wc: 'Direct EBS Payables interface + custom integrations',
                      ez: 'Oracle Payables Open Interface — Oracle-supported, no base-table writes',
                    },
                    {
                      cap: 'Fusion integration',
                      wc: 'Not natively supported — requires re-platform',
                      ez: 'Oracle Integration Cloud (OIC) + FBDI load with real-time validation',
                    },
                    {
                      cap: 'Supplier email exceptions',
                      wc: 'Manual triage by AP team',
                      ez: 'AI email exception prevention upstream of the workflow',
                    },
                    {
                      cap: 'Version coverage',
                      wc: 'WebCenter 11g, 12c, 14c',
                      ez: 'Reads all three; modernizes forward independent of source version',
                    },
                  ].map((row) => (
                    <tr key={row.cap} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">{row.cap}</td>
                      <td className="px-5 py-4 text-gray-700">{row.wc}</td>
                      <td className="px-5 py-4 text-gray-700">
                        <strong className="font-semibold text-gray-900">{row.ez}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-sm text-gray-500 mt-6 max-w-2xl mx-auto">
              EZ Cloud preserves the Oracle support posture across the modernization — Oracle
              Payables Open Interface on EBS, Oracle Integration Cloud and FBDI on Fusion. No
              base-table writes.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
                  How we approach the work
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  We read your implementation directly. Your team focuses on validation.
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Instead of asking your team to reconstruct documentation from years ago, our team
                  reads your existing WebCenter configuration in place — Forms Recognition projects,
                  SOA workflows, FIPSA accelerators, EBS or Fusion integration code — and translates
                  the business requirements forward to a modern AP automation platform.
                </p>
                <div className="space-y-4">
                  {HOW_IT_WORKS.map((item) => (
                    <Card
                      key={item.title}
                      className="p-5 bg-white border border-gray-200 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2
                          className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
                          aria-hidden
                        />
                        <div>
                          <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                          <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
              <Card className="p-7 bg-white border border-gray-200 shadow-md">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-5">
                  The path forward
                </p>
                <div className="space-y-2">
                  {PATH_STEPS.map((step, i) => (
                    <div key={step.step}>
                      <div className="border-l-[3px] border-accent bg-accent/10 px-5 py-4 rounded-r-lg">
                        <p className="text-sm font-bold text-gray-900 mb-1">
                          {step.step} · {step.title}
                        </p>
                        <p className="text-[13px] text-gray-700 leading-relaxed">{step.body}</p>
                      </div>
                      {i < PATH_STEPS.length - 1 && (
                        <div className="text-center text-accent text-xl py-1">
                          <ArrowDown className="w-5 h-5 inline" aria-hidden />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Fusion bridge */}
        <section id="bridge" className="py-16 bg-gradient-to-br from-accent/5 to-white border-t border-gray-200">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
                  The Fusion bridge scenario
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Use AP modernization to unlock your Fusion migration
                </h2>
                <p className="text-gray-600 leading-relaxed mb-5">
                  One of the most common patterns we see: a customer is planning the move from
                  Oracle E-Business Suite to Oracle Fusion Cloud ERP, and their existing AP
                  automation complexity is one of the things making the migration harder. Modernizing
                  AP first — on EBS — can be the unlock that makes the broader Fusion migration
                  tractable.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  EZ Cloud is built to work this way. The same platform, the same integration
                  patterns, the same operational model run against both Oracle EBS and Oracle Fusion.
                  Your AP modernization is reusable across the migration — not re-implemented after
                  it.
                </p>
              </div>
              <Card className="p-7 bg-white border border-gray-200 shadow-md">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-5">
                  Two paths, one platform
                </p>
                <p className="text-sm font-bold text-gray-900 mb-3">
                  Path A · Modernize on EBS, stay on EBS
                </p>
                <div className="flex items-center gap-2 flex-wrap mb-6">
                  <span className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-gray-900">
                    WebCenter AP
                  </span>
                  <ArrowRight className="w-4 h-4 text-accent" aria-hidden />
                  <span className="bg-primary text-white rounded-lg px-3 py-2 text-xs font-semibold">
                    EZ Cloud on EBS
                  </span>
                </div>
                <p className="text-sm font-bold text-gray-900 mb-3">
                  Path B · Modernize on EBS, then bridge to Fusion
                </p>
                <div className="flex items-center gap-2 flex-wrap mb-6">
                  <span className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-gray-900">
                    WebCenter AP
                  </span>
                  <ArrowRight className="w-4 h-4 text-accent" aria-hidden />
                  <span className="bg-primary text-white rounded-lg px-3 py-2 text-xs font-semibold">
                    EZ Cloud on EBS
                  </span>
                  <ArrowRight className="w-4 h-4 text-accent" aria-hidden />
                  <span className="bg-primary text-white rounded-lg px-3 py-2 text-xs font-semibold">
                    EZ Cloud on Fusion
                  </span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed pt-5 border-t border-gray-200">
                  Same EZ Cloud platform either way. The integration layer swaps from Oracle Payables
                  Open Interface to Oracle Integration Cloud — the business logic, configurations,
                  and operational model come with you.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Assessment */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <Card className="p-8 md:p-12 bg-white border border-gray-200 shadow-md">
              <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 text-accent font-bold text-xs uppercase tracking-wide px-3 py-1.5 rounded-md mb-4">
                <ClipboardList className="w-4 h-4" aria-hidden />
                Productized engagement
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                The WebCenter AP Modernization Assessment
              </h3>
              <p className="text-gray-600 max-w-3xl leading-relaxed">
                A fixed-scope, fixed-fee engagement that produces a forward-modernization plan for
                your existing Oracle WebCenter AP implementation. The deliverable is yours to keep
                whether you proceed to implementation with EZ Cloud or not.
              </p>
              <div className="grid md:grid-cols-3 gap-5 mt-8">
                {ASSESSMENT_PILLS.map((pill) => (
                  <div
                    key={pill.title}
                    className="p-6 bg-gray-50 border border-gray-200 rounded-xl"
                  >
                    <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                      {pill.title}
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed">{pill.body}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* Engagement tiers — captures the full range of WebCenter consulting
            buyers, including those who choose to stay on WebCenter via OCI
            lift-and-shift or 12c → 14c upgrade. Widens the funnel beyond the
            "modernize off WebCenter" buyer to Path A buyers as well. */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How we work with Oracle WebCenter customers
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four ways our 25 years of WebCenter expertise apply
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Different WebCenter customers are in different places. The same founder-level WebCenter
              expertise — Forms Recognition, SOA, WebLogic, FIPSA, ADF, across 11g, 12c, and 14c —
              applies across every engagement tier.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 1 · Modernization Assessment
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fixed-scope, fixed-fee forward-modernization plan
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  The productized engagement above. Andrew reads your existing WebCenter
                  implementation directly and delivers a forward-modernization plan that's yours to
                  keep — whether you proceed to implementation with EZ Cloud or not. The universal
                  entry point for any WebCenter AP customer facing the Fusion Middleware 12c support
                  decision.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 2 · Architecture Review
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  A deeper read of your existing WebCenter implementation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A one-week fixed-fee read of your running WebCenter AP — Forms Recognition rules,
                  SOA composites, WebLogic configuration, FIPSA accelerators, ADF customizations,
                  EBS or Fusion integration code. Output: a structured inventory of what you have,
                  where the complexity lives, and what each layer would mean in a modernization or
                  upgrade decision.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 3 · Strategic Advisory Retainer
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Founder-level guidance through your decision window
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A monthly retainer with Andrew, sized for the customer working through a
                  multi-quarter WebCenter decision with internal stakeholders, procurement, and
                  Oracle. Strategic guidance on architecture, upgrade vs modernization tradeoffs,
                  Fusion migration sequencing, and the implications of the December 2026 support
                  deadline.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 4 · Specific WebCenter consulting outcomes
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Project-based WebCenter work
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Forms Recognition rules migration audits, FIPSA-specific assessments, WebCenter
                  Imaging archive migrations, 12c &rarr; 14c upgrade planning, OCI architecture and
                  security setup, custom SOA and ADF integration work. Project-based, founder-led,
                  scoped to a defined outcome rather than open-ended hourly consulting.
                </p>
              </Card>
            </div>

            {/* Path A buyer welcome — explicit invitation for customers who
                are choosing to stay on WebCenter via OCI lift-and-shift. */}
            <Card className="mt-8 p-7 md:p-10 bg-accent/5 border border-accent/30 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                Not ready to modernize off WebCenter yet?
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                Path A — lift-and-shift to OCI, or upgrade to 14c — is a valid choice too
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Some customers facing the December 2026 Fusion Middleware 12c support deadline will
                choose to lift-and-shift their WebCenter AP implementation onto Oracle Cloud
                Infrastructure (via the Oracle WebCenter for OCI Marketplace images) or upgrade in
                place to WebCenter 14c. Both are legitimate forward paths.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The 25-year founder-level WebCenter expertise that powers EZ Cloud's modernization
                assessments is equally available for these engagements — architecture review,
                FIPSA-specific assessments, Forms Recognition rules migration audits, 12c &rarr; 14c
                upgrade planning, and OCI-side setup. Whichever path you choose, the WebCenter AP
                expertise behind the decision is the same.
              </p>
            </Card>
          </div>
        </section>

        {/* Deep-dive sub-pages — topic cluster cross-link. Pushes internal
            authority to each spoke and gives buyers a way to drill into
            specific WebCenter topics. */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Deep dives
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Go deeper on specific Oracle WebCenter AP topics
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The WebCenter AP modernization decision has several specific surfaces — the December
              2026 support timeline, the Path A vs. Path B comparison, FIPSA-specific
              modernization, WFR-specific modernization. Each has its own dedicated page.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/solutions/webcenter-12c-end-of-support-decision" className="group">
                <Card className="p-7 bg-white border border-gray-200 shadow-sm group-hover:border-accent transition-colors h-full">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    The forcing function
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-accent transition-colors">
                    WebCenter 12c End of Support — The December 2026 Decision
                  </h3>
                  <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
                    Oracle Fusion Middleware 12c Premier Support ends December 2026. Extended
                    Support ends December 2027. Three forward paths explained.
                  </p>
                  <span className="inline-flex items-center font-semibold text-primary group-hover:text-accent transition-colors">
                    See the decision framework
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </span>
                </Card>
              </Link>

              <Link to="/solutions/webcenter-oci-marketplace-vs-modernize" className="group">
                <Card className="p-7 bg-white border border-gray-200 shadow-sm group-hover:border-accent transition-colors h-full">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    Path A vs. Path B
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-accent transition-colors">
                    Oracle WebCenter on OCI Marketplace vs. Modernize Off WebCenter
                  </h3>
                  <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
                    The structured comparison of lift-and-shift to OCI Marketplace versus
                    modernizing off WebCenter to a modern AP automation platform.
                  </p>
                  <span className="inline-flex items-center font-semibold text-primary group-hover:text-accent transition-colors">
                    See the comparison
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </span>
                </Card>
              </Link>

              <Link to="/solutions/modernize-webcenter-fipsa" className="group">
                <Card className="p-7 bg-white border border-gray-200 shadow-sm group-hover:border-accent transition-colors h-full">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    FIPSA-specific
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-accent transition-colors">
                    Modernize FIPSA — Financial Imaging Processing Solutions Accelerator
                  </h3>
                  <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
                    How EZ Cloud absorbs the FIPSA workflow shape — WCI, WFR, SOA, EBS Payables
                    integration — into a modern AI-driven AP automation platform.
                  </p>
                  <span className="inline-flex items-center font-semibold text-primary group-hover:text-accent transition-colors">
                    See FIPSA modernization
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </span>
                </Card>
              </Link>

              <Link to="/solutions/modernize-webcenter-forms-recognition" className="group">
                <Card className="p-7 bg-white border border-gray-200 shadow-sm group-hover:border-accent transition-colors h-full">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    WFR-specific
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-accent transition-colors">
                    Modernize Oracle WebCenter Forms Recognition (WFR)
                  </h3>
                  <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
                    Replace per-supplier WFR templates with AI extraction that reads unseen
                    invoices and learns from corrections. No template authoring.
                  </p>
                  <span className="inline-flex items-center font-semibold text-primary group-hover:text-accent transition-colors">
                    See WFR modernization
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </span>
                </Card>
              </Link>

              <Link to="/solutions/webcenter-14c-upgrade" className="group">
                <Card className="p-7 bg-white border border-gray-200 shadow-sm group-hover:border-accent transition-colors h-full">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    Path A+ — Upgrade
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-accent transition-colors">
                    Oracle WebCenter 12c to 14c Upgrade
                  </h3>
                  <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
                    The out-of-place upgrade path. 14c brings REST APIs, OCI Object Storage,
                    cloud-native architecture, and AI integration. ~8 years of supported runway.
                  </p>
                  <span className="inline-flex items-center font-semibold text-primary group-hover:text-accent transition-colors">
                    See the 14c upgrade path
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* Complete WebCenter solutions library — flat 4-column link grid covering
            every WebCenter buyer-query landing page. Card grid above shows the
            top 5; this section catches the long tail without overwhelming the UI. */}
        <section className="py-14 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Complete library
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3">
              Every Oracle WebCenter buyer-query landing page in one place
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto text-[15px]">
              Whatever your specific WebCenter scenario — a component decision, a vertical posture, or a comparison — there is a dedicated page here.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 text-[14px]">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                  Components
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/solutions/modernize-webcenter-forms-recognition" className="text-gray-700 hover:text-accent transition-colors">
                      Modernize WebCenter Forms Recognition
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-forms-recognition-experts" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter Forms Recognition Experts
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/modernize-webcenter-imaging" className="text-gray-700 hover:text-accent transition-colors">
                      Modernize WebCenter Imaging
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/modernize-webcenter-enterprise-capture" className="text-gray-700 hover:text-accent transition-colors">
                      Modernize WebCenter Enterprise Capture
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/modernize-webcenter-fipsa" className="text-gray-700 hover:text-accent transition-colors">
                      Modernize FIPSA
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/oracle-adf-non-po-coding-form-modernization" className="text-gray-700 hover:text-accent transition-colors">
                      ADF Non-PO Coding Form Modernization
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/oracle-ebs-managed-attachments-modernization" className="text-gray-700 hover:text-accent transition-colors">
                      EBS Managed Attachments Modernization
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                  Stack &amp; infrastructure
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/solutions/oracle-soa-suite-migration" className="text-gray-700 hover:text-accent transition-colors">
                      Oracle SOA Suite Migration
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/oracle-weblogic-14c-upgrade" className="text-gray-700 hover:text-accent transition-colors">
                      Oracle WebLogic 14c Upgrade
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/oracle-bpm-suite-migration" className="text-gray-700 hover:text-accent transition-colors">
                      Oracle BPM Suite Migration
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-12c-end-of-support-decision" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter 12c End of Support
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-14c-upgrade" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter 12c &rarr; 14c Upgrade
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-11g-to-14c" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter 11g &rarr; 14c
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-sustaining-support-decision" className="text-gray-700 hover:text-accent transition-colors">
                      Sustaining Support &mdash; the wait-it-out math
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                  Industry &amp; vertical
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/solutions/webcenter-healthcare" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter for Healthcare
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-higher-education" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter for Higher Education
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-k12" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter for K-12 Districts
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-government-public-sector" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter for Government &amp; Public Sector
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/healthcare-oracle-fusion" className="text-gray-700 hover:text-accent transition-colors">
                      Healthcare on Oracle Fusion
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/education-oracle-ebs" className="text-gray-700 hover:text-accent transition-colors">
                      Education on Oracle EBS
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                  Strategy &amp; comparison
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/solutions/webcenter-modernization-services" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter Modernization Services
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-oci-marketplace-vs-modernize" className="text-gray-700 hover:text-accent transition-colors">
                      OCI Marketplace vs Modernize
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-to-fusion-ap-migration" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter &rarr; Fusion AP Migration
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/wfr-vs-oci-document-understanding" className="text-gray-700 hover:text-accent transition-colors">
                      WFR vs OCI Document Understanding
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-records-retention-modernization" className="text-gray-700 hover:text-accent transition-colors">
                      Records Retention Modernization
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/webcenter-content-alternatives" className="text-gray-700 hover:text-accent transition-colors">
                      WebCenter Content Alternatives
                    </Link>
                  </li>
                  <li>
                    <Link to="/solutions/is-oracle-killing-webcenter" className="text-gray-700 hover:text-accent transition-colors">
                      Oracle WebCenter in 2026 &mdash; Lifecycle &amp; Forward Paths
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Founder */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The Founder
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              25 years across the full Oracle WebCenter AP stack
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
              Founder-led delivery. The person reading your WebCenter Forms Recognition projects and
              Oracle SOA composites is the same person who'll architect your modern AP automation on
              EZ Cloud.
            </p>
            <Card className="p-8 md:p-12 bg-white border border-gray-200 shadow-md">
              <div className="grid md:grid-cols-[200px_1fr] gap-8 md:gap-12 items-center">
                <img
                  src={FOUNDER.photo}
                  alt={FOUNDER.photoAlt}
                  width={176}
                  height={176}
                  className="w-44 h-44 mx-auto md:mx-0 rounded-full object-cover border border-gray-200 shadow-xl"
                />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1" id="andrew-blackman">
                    {FOUNDER.name}
                  </h3>
                  <p className="text-gray-500 font-semibold mb-5 text-sm">
                    {FOUNDER.role}, {FOUNDER.company}
                  </p>
                  <p className="text-gray-900 text-lg font-medium leading-relaxed italic border-l-[3px] border-accent pl-6 mb-5">
                    &ldquo;I've spent my career working with Oracle WebCenter AP automation customers
                    across 11g, 12c, and 14c. The stack is sophisticated, every implementation is
                    unique, and the artifacts — Forms Recognition, SOA, WebLogic, FIPSA, ADF — tell
                    the whole story when you know how to read them. That's what we bring.&rdquo;
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    25 years across Oracle WebCenter Enterprise Capture, Forms Recognition, SOA
                    Suite, WebLogic, FIPSA, WebCenter Content, and WebCenter Imaging. EZ Cloud's
                    reference architecture for Oracle Cloud Infrastructure is published on
                    docs.oracle.com.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Client engagements — Fortune 500 social proof from Andrew's
            consulting career. Direct authority signal: the buyer sees that
            Andrew has done this work at this scale before. */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Selected client engagements
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where Andrew's WebCenter expertise has been applied
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              25 years of Oracle WebCenter consulting across Fortune 500 enterprises and major
              public-sector institutions. Most recent: an Oracle Consulting Services engagement
              at Via Rail (12/2025 – 04/2026) delivering AP automation on Oracle WebCenter Forms
              Recognition integrated with Oracle E-Business Suite R12 and Oracle Fusion Cloud ERP.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { sector: 'Transportation', name: 'Via Rail Canada' },
                { sector: 'Transportation', name: 'Metropolitan Transportation Authority of New York' },
                { sector: 'Financial services', name: 'Equifax' },
                { sector: 'Financial services', name: 'Marsh & McLennan' },
                { sector: 'Financial services', name: 'Lincoln Financial Group' },
                { sector: 'Financial services', name: 'Experian' },
                { sector: 'Healthcare', name: 'Blue Cross Blue Shield' },
                { sector: 'Healthcare', name: 'Harvard Pilgrim Healthcare' },
                { sector: 'Government', name: 'US House of Representatives' },
                { sector: 'Government', name: 'US Department of Agriculture' },
                { sector: 'Government', name: 'State of Alaska' },
                { sector: 'Industrial', name: 'Archer Daniels Midland' },
                { sector: 'Industrial', name: 'ASML' },
                { sector: 'Technology', name: 'Wipro / AboveNet' },
                { sector: 'Technology', name: 'Singapore Stock Exchange' },
              ].map((c) => (
                <div
                  key={c.name}
                  className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1">
                    {c.sector}
                  </p>
                  <p className="text-gray-900 font-semibold text-sm">{c.name}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Consulting engagements across the {FOUNDER.name} career. Not EZ Cloud customer
              references. Listed to convey the depth and breadth of Oracle WebCenter expertise
              available on EZ Cloud Modernization engagements.
            </p>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Let's talk about your WebCenter AP implementation
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute discovery call with our founder. We'll walk you through the modernization
              path, talk through your current WebCenter footprint, and tell you honestly whether EZ
              Cloud is the right fit.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                Book a 30-minute call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/integrations/oracle">Explore Oracle AP automation</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModernizeOracleWebCenter;
